import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    MentorshipRequestDTO,
    CreateMentorshipRequestDTO,
    RespondToRequestDto,
    FacilitateMentorshipRequestDto,
    AddRequestCommentDto,
    ApiResponse
} from '../../../shared/types';
import axiosService from '../services/axios.service';
import { mentorKeys } from './useMentors';
import { mentorshipKeys } from './useMentorships';
import { useCrewModuleEnabled } from './useCrews';
import { profileKeys } from './useProfiles';

// Query key factory
export const mentorshipRequestKeys = {
    all: ['mentorshipRequests'] as const,
    crews: (crewSlug: string) => [...mentorshipRequestKeys.all, 'crew', crewSlug] as const,
    pending: (crewSlug: string) => [...mentorshipRequestKeys.crews(crewSlug), 'pending'] as const,
    myRequests: (crewSlug: string, status?: string) =>
    [...mentorshipRequestKeys.crews(crewSlug), 'my-requests', status] as const,
    allRequests: (crewSlug: string, status?: string) =>
    [...mentorshipRequestKeys.crews(crewSlug), 'all', status] as const,
    detail: (crewSlug: string, requestId: string) =>
    [...mentorshipRequestKeys.crews(crewSlug), 'detail', requestId] as const
};

// API functions
const mentorshipRequestApi = {
    // Create a new mentorship request (mentee action)
    createRequest: async (crewSlug: string, data: CreateMentorshipRequestDTO): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data: response } = await axiosService.post<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests`,
            data
        );
        return response;
    },

    // Get my outgoing requests (as mentee)
    getMyRequests: async (crewSlug: string, status?: string): Promise<ApiResponse<MentorshipRequestDTO[]>> => {
        const { data } = await axiosService.get<ApiResponse<MentorshipRequestDTO[]>>(
            `/c/${crewSlug}/mentorship-requests/my-requests`,
            { params: { status } }
        );
        return data;
    },

    // Get pending requests for me (as mentor)
    getPendingRequests: async (crewSlug: string): Promise<ApiResponse<MentorshipRequestDTO[]>> => {
        const { data } = await axiosService.get<ApiResponse<MentorshipRequestDTO[]>>(
            `/c/${crewSlug}/mentorship-requests/pending`
        );
        return data;
    },

    // Withdraw a request (mentee action)
    withdrawRequest: async (crewSlug: string, requestId: string): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data } = await axiosService.delete<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests/${requestId}/withdraw`
        );
        return data;
    },

    // Update request focus areas (mentee action)
    updateRequestFocusAreas: async (crewSlug: string, requestId: string, focusAreas: string[]): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data } = await axiosService.patch<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests/${requestId}/focus-areas`,
            { focusAreas }
        );
        return data;
    },

    // Respond to a request (mentor action - accept or reject)
    respondToRequest: async (
        crewSlug: string,
        requestId: string,
        response: RespondToRequestDto
    ): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data } = await axiosService.post<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests/${requestId}/respond`,
            response
        );
        return data;
    },

    // Get all requests in crew (admin only)
    getAllRequests: async (crewSlug: string, status?: string): Promise<ApiResponse<MentorshipRequestDTO[]>> => {
        const { data } = await axiosService.get<ApiResponse<MentorshipRequestDTO[]>>(
            `/c/${crewSlug}/mentorship-requests/all`,
            { params: { status } }
        );
        return data;
    },

    // Facilitate a match (admin action)
    facilitateRequest: async (crewSlug: string, data: FacilitateMentorshipRequestDto): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data: response } = await axiosService.post<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests/facilitate`,
            data
        );
        return response;
    },

    // Cancel a request (admin action)
    cancelRequest: async (crewSlug: string, requestId: string): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data } = await axiosService.delete<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests/${requestId}/cancel`
        );
        return data;
    },

    // Get single request details
    getRequestById: async (crewSlug: string, requestId: string): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data } = await axiosService.get<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests/${requestId}`
        );
        return data;
    },

    // Add a comment to a request
    addComment: async (crewSlug: string, requestId: string, comment: AddRequestCommentDto): Promise<ApiResponse<MentorshipRequestDTO>> => {
        const { data } = await axiosService.post<ApiResponse<MentorshipRequestDTO>>(
            `/c/${crewSlug}/mentorship-requests/${requestId}/comments`,
            comment
        );
        return data;
    }
};

// React Query hooks

// Get my outgoing requests (as mentee)
export const useMyMentorshipRequests = (crewSlug: string, status?: string) => {
    const mentorshipEnabled = useCrewModuleEnabled(crewSlug, 'mentorship');
    return useQuery({
        queryKey: mentorshipRequestKeys.myRequests(crewSlug, status),
        queryFn: () => mentorshipRequestApi.getMyRequests(crewSlug, status),
        enabled: !!crewSlug && mentorshipEnabled,
        refetchInterval: 5 * 60 * 1000 // Poll every 5 minutes for new comments
    });
};

// Get pending requests for me (as mentor)
export const usePendingMentorshipRequests = (crewSlug: string) => {
    const mentorshipEnabled = useCrewModuleEnabled(crewSlug, 'mentorship');
    return useQuery({
        queryKey: mentorshipRequestKeys.pending(crewSlug),
        queryFn: () => mentorshipRequestApi.getPendingRequests(crewSlug),
        enabled: !!crewSlug && mentorshipEnabled,
        refetchOnWindowFocus: true,
        refetchInterval: 5 * 60 * 1000 // Poll every 5 minutes for new comments
    });
};

// Get all requests in crew (admin only)
export const useAllMentorshipRequests = (crewSlug: string, status?: string) => {
    const mentorshipEnabled = useCrewModuleEnabled(crewSlug, 'mentorship');
    return useQuery({
        queryKey: mentorshipRequestKeys.allRequests(crewSlug, status),
        queryFn: () => mentorshipRequestApi.getAllRequests(crewSlug, status),
        enabled: !!crewSlug && mentorshipEnabled
    });
};

// Get single request by ID
export const useMentorshipRequest = (crewSlug: string, requestId: string) => {
    return useQuery({
        queryKey: mentorshipRequestKeys.detail(crewSlug, requestId),
        queryFn: () => mentorshipRequestApi.getRequestById(crewSlug, requestId),
        enabled: !!crewSlug && !!requestId,
        refetchInterval: 5 * 60 * 1000 // Poll every 5 minutes for new comments
    });
};

// Create a new mentorship request
export const useCreateMentorshipRequest = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateMentorshipRequestDTO) => mentorshipRequestApi.createRequest(crewSlug, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
        }
    });
};

// Withdraw a request
export const useWithdrawMentorshipRequest = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (requestId: string) => mentorshipRequestApi.withdrawRequest(crewSlug, requestId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
        }
    });
};
// Update request focus areas
export const useUpdateRequestFocusAreas = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ requestId, focusAreas }: { requestId: string; focusAreas: string[] }) =>
            mentorshipRequestApi.updateRequestFocusAreas(crewSlug, requestId, focusAreas),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
        }
    });
};
// Respond to a request (accept or reject)
export const useRespondToMentorshipRequest = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ requestId, response }: { requestId: string; response: RespondToRequestDto }) =>
            mentorshipRequestApi.respondToRequest(crewSlug, requestId, response),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
            // Accepting updates the mentor's Profile.currentMentees (available-slots count),
            // which is embedded directly on the profile the mentor's own page displays.
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
        }
    });
};

// Facilitate a match (admin action)
export const useFacilitateMentorshipRequest = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: FacilitateMentorshipRequestDto) => mentorshipRequestApi.facilitateRequest(crewSlug, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
        }
    });
};

// Cancel a request (admin action)
export const useCancelMentorshipRequest = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (requestId: string) => mentorshipRequestApi.cancelRequest(crewSlug, requestId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
        }
    });
};

// Add a comment to a request
export const useAddRequestComment = (crewSlug: string, requestId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (comment: AddRequestCommentDto) => mentorshipRequestApi.addComment(crewSlug, requestId, comment),
        onSuccess: (response) => {
            const updated = response.data;
            if (!updated) return;

            // Immediately update the detail cache so the new comment is visible at once
            queryClient.setQueryData<ApiResponse<MentorshipRequestDTO>>(
                mentorshipRequestKeys.detail(crewSlug, requestId),
                (old) => old ? { ...old, data: updated } : old
            );

            // Patch any list caches that contain this request
            queryClient.setQueriesData<ApiResponse<MentorshipRequestDTO[]>>(
                { queryKey: mentorshipRequestKeys.crews(crewSlug) },
                (old) => {
                    if (!old?.data) return old;
                    return {
                        ...old,
                        data: old.data.map((r) => r._id === requestId ? updated : r)
                    };
                }
            );

            // Background refetch for eventual consistency
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.detail(crewSlug, requestId) });
        }
    });
};
